import { supabase } from "@/integrations/supabase/client";
import { Notification } from "./types";

export const createNotificationActions = (
  user: any,
  notifications: Notification[] | null,
  setNotifications: React.Dispatch<React.SetStateAction<Notification[] | null>>,
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>,
) => {
  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notificationId)
        .eq("user_id", user.id);

      if (error) {
        throw error;
      }

      // Update local state
      setNotifications(
        (prev) =>
          prev?.map((n) =>
            n.id === notificationId ? { ...n, is_read: true } : n,
          ) || null,
      );

      // Decrement unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user || !notifications) return;

    try {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) {
        throw error;
      }

      // Update local state
      setNotifications(
        (prev) => prev?.map((n) => ({ ...n, is_read: true })) || null,
      );

      // Reset unread count
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId)
        .eq("user_id", user.id);

      if (error) {
        throw error;
      }

      // Update local state
      setNotifications(
        (prev) => prev?.filter((n) => n.id !== notificationId) || null,
      );

      // Update unread count if the deleted notification was unread
      const wasUnread =
        notifications?.find((n) => n.id === notificationId)?.is_read === false;
      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  return {
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
};
